/**
 * Widget flow route subtree — the SINGLE source of the `restaurant/:id` child
 * routes, imported by BOTH the SPA (`src/index.tsx`, under `createBrowserRouter`)
 * and the embed (`WidgetRoot.tsx`, under `createMemoryRouter`). Keeping one
 * definition means the two surfaces can never drift. The set is pure route
 * config (no router-kind dependency, no embed-shell imports), so it behaves
 * identically under both routers. Legacy aliases are included so the SPA keeps
 * honoring old deep-links; they're harmless in the embed (which always boots at
 * `booking`).
 */
import type { RouteObject } from 'react-router-dom';
import { redirect } from 'react-router-dom';

import WidgetBooking from './Booking/Booking.tsx';
import DetailsPage from './Details/DetailsPage.tsx';
import VerifyPage from './Verify/VerifyPage.tsx';
import PaymentPage from './Payment/PaymentPage.tsx';
import CheckoutPage from './Checkout/CheckoutPage.tsx';
import ConfirmedPage from './Confirmed/ConfirmedPage.tsx';
import CustomRequestPage from './CustomRequest/CustomRequestPage.tsx';
import CustomRequestStepsPage from './CustomRequest/CustomRequestStepsPage.tsx';
import CustomRequestCheckoutPage from './CustomRequest/CustomRequestCheckoutPage.tsx';
import CustomRequestSubmittedPage from './CustomRequest/CustomRequestSubmittedPage.tsx';

export const widgetChildRoutes: RouteObject[] = [
  {
    path: '',
    loader: ({ request }: { request: Request }) => {
      const url = new URL(request.url);
      return redirect(`./booking${url.search}`);
    },
  },
  { path: 'booking', element: <WidgetBooking /> },
  { path: 'details', element: <DetailsPage /> },
  { path: 'verify', element: <VerifyPage /> },
  { path: 'payment', element: <PaymentPage /> },
  { path: 'checkout', element: <CheckoutPage /> },
  { path: 'confirmed', element: <ConfirmedPage /> },
  { path: 'confirmed/:uuid', element: <ConfirmedPage /> },
  { path: 'request', element: <CustomRequestPage /> },
  { path: 'request/steps', element: <CustomRequestStepsPage /> },
  { path: 'request/checkout', element: <CustomRequestCheckoutPage /> },
  { path: 'request/submitted', element: <CustomRequestSubmittedPage /> },
  { path: 'request/submitted/:uuid', element: <CustomRequestSubmittedPage /> },

  // Legacy redirect aliases — old deep-links still resolve (SPA); unused in embed.
  { path: 'info', loader: () => redirect('../details') },
  { path: 'eventinfo', loader: () => redirect('../details') },
  { path: 'phone', loader: () => redirect('../verify') },
  { path: 'eventphone', loader: () => redirect('../verify') },
  { path: 'eventpayment', loader: () => redirect('../payment') },
  { path: 'confirm', loader: () => redirect('../checkout') },
  { path: 'eventconfirm', loader: () => redirect('../checkout') },
  { path: 'confirmation', loader: () => redirect('../confirmed') },
  { path: 'eventconfirmation', loader: () => redirect('../confirmed') },
];
